/**
 * Snaps a number to the nearest multiple of another number
 * @param num         the number to snap
 * @param multiple    `num` will be snapped to the nearest multiple of this number
 * @param snapType    how to snap `num`, defaults to `SnapType.Round`
 */
export function snapToNearestMultiple(
  num: number,
  multiple: number,
  snapType: SnapType = SnapType.Round
) {
  if (multiple === 0) {
    return 0
  }

  switch (snapType) {
    case SnapType.Round:
      return Math.round(num / multiple) * multiple
    case SnapType.Floor:
      return Math.floor(num / multiple) * multiple
    case SnapType.Ceil:
      return Math.ceil(num / multiple) * multiple
  }
}

/** A way to snap the number in `snapToNearestMultiple` */
export enum SnapType {
  // use Math.round
  Round,
  // use Math.floor
  Floor,
  // use Math.ceil
  Ceil,
}

/**
 * Given a y value within the range of a line segment, calculates the associated
 * x value along the line. If y is out of range, returns undefined.
 * @param p1 endpoint of line
 * @param p2 endpoint of line
 * @returns x coordinate
 */
export function xAlongLine(
  p1: [number, number],
  p2: [number, number],
  y: number
): number {
  const [x1, y1] = p1
  const [x2, y2] = p2
  if (y2 === y1) return x1
  return ((y - y1) / (y2 - y1)) * (x2 - x1) + x1
}

/**
 * Given an x value within the range of a line segment, calculates the associated
 * y value along the line. If x is out of range, returns undefined.
 * @param p1 endpoint of line
 * @param p2 endpoint of line
 * @param x
 * @returns y coordinate
 */
export function yAlongLine(
  p1: [number, number],
  p2: [number, number],
  x: number
): number {
  const [x1, y1] = p1
  const [x2, y2] = p2
  if (x2 === x1) return y1
  return ((x - x1) / (x2 - x1)) * (y2 - y1) + y1
}
